import { createGlobalStyle, css } from 'styled-components';

import { Theme } from 'utils/color/horizonTheme';

/**
 * This is global css passed a s a child of ThemeProvider in
 * `src/horizon/index.tsx`. It should contain only global styles that
 * don't fit nicely as a style of a specific component, e.g. scrollbars.
 *
 * TODO: consider moving appropriate items from App.css here.
 */

export const GlobalStyles = createGlobalStyle`
  ${({ theme }) => css`
    body {
      font-family: 'Inter', sans-serif !important;
      background: ${theme.primaryBackgroundColor};
    }

    /* Brand headlines use Open Sans; body copy stays Inter (Brand Guidelines V3). */
    h1,
    h2,
    h3,
    h4,
    h5,
    h6,
    .ant-typography h1,
    .ant-typography h2,
    .ant-typography h3,
    .ant-typography h4,
    .ant-typography h5,
    h1.ant-typography,
    h2.ant-typography,
    h3.ant-typography,
    h4.ant-typography,
    h5.ant-typography {
      font-family: 'Open Sans', sans-serif;
    }

    a:hover {
      color: ${theme.actionTextHover};
    }

    /*
     * antd v4 components derive their primary color from the build-time
     * LESS variable (still SkySpecs teal), so re-point the common v4 primary
     * surfaces to the platform action color so they match the antd v5 /
     * styled-components UI.
     *
     * antd v4 and v5 share the 'ant' class prefix in this app, so these
     * also match v5 controls — harmless, since v5 already renders these in
     * theme.teal via ConfigProvider. The :not(...-disabled) guards keep
     * antd's muted disabled styling for both versions (and raise specificity
     * enough to win over v4's base rules without !important).
     */
    .ant-checkbox-checked:not(.ant-checkbox-disabled) .ant-checkbox-inner {
      background-color: ${theme.teal};
      border-color: ${theme.teal};
    }
    .ant-checkbox-wrapper:not(.ant-checkbox-wrapper-disabled):hover .ant-checkbox-inner,
    .ant-checkbox:not(.ant-checkbox-disabled):hover .ant-checkbox-inner,
    .ant-checkbox-input:focus + .ant-checkbox-inner {
      border-color: ${theme.teal};
    }
    .ant-checkbox-indeterminate:not(.ant-checkbox-disabled) .ant-checkbox-inner::after {
      background-color: ${theme.teal};
    }

    .ant-radio-checked:not(.ant-radio-disabled) .ant-radio-inner {
      border-color: ${theme.teal};
    }
    .ant-radio-checked:not(.ant-radio-disabled) .ant-radio-inner::after {
      background-color: ${theme.teal};
    }
    .ant-radio:not(.ant-radio-disabled):hover .ant-radio-inner,
    .ant-radio-input:focus + .ant-radio-inner {
      border-color: ${theme.teal};
    }

    /*
     * Button-style Radio groups draw their surface from antd's light palette,
     * leaving unselected options white on the dark theme. Same tokens as the
     * components/ui Button, and navy on the solid fill as primary buttons use.
     */
    .ant-radio-group .ant-radio-button-wrapper {
      background: ${theme.buttonBackgroundColor};
      border-color: ${theme.buttonBorderColor};
      color: ${theme.headerColor};
    }
    .ant-radio-group .ant-radio-button-wrapper span {
      color: inherit;
    }
    .ant-radio-group .ant-radio-button-wrapper:not(:first-child)::before {
      background: ${theme.buttonBorderColor};
    }
    .ant-radio-group.ant-radio-group-solid
      .ant-radio-button-wrapper-checked:not(.ant-radio-button-wrapper-disabled) {
      background: ${theme.teal};
      border-color: ${theme.teal};
      color: ${theme.solidActionText};

      &:hover {
        background: ${theme.tealActive};
        border-color: ${theme.tealActive};
        color: ${theme.solidActionText};
      }
    }

    .ant-switch-checked:not(.ant-switch-disabled) {
      background-color: ${theme.teal};
    }
    /*
     * Unchecked Switch tracks use antd's light-mode translucent black, which
     * disappears on dark navy surfaces. Give them a visible track in dark mode
     * only — light mode keeps antd's default.
     */
    ${theme.themeName === Theme.Dark
      ? css`
          .ant-switch:not(.ant-switch-checked) {
            background: ${theme.borderColor};
          }

          .ant-tabs.ant-tabs-top > .ant-tabs-nav::before {
            border-bottom-color: ${theme.borderColor};
          }
        `
      : ''}

    .ant-tabs-ink-bar {
      background: ${theme.teal};
    }
    .ant-tabs-tab:not(.ant-tabs-tab-disabled):hover,
    .ant-tabs-tab.ant-tabs-tab-active .ant-tabs-tab-btn {
      color: ${theme.actionText};
    }

    .ant-slider:not(.ant-slider-disabled) .ant-slider-track {
      background-color: ${theme.teal};
    }
    .ant-slider:not(.ant-slider-disabled) .ant-slider-handle {
      border-color: ${theme.teal};
    }

    .ant-pagination-item-active {
      border-color: ${theme.actionText};
    }
    .ant-pagination-item-active a {
      color: ${theme.actionText};
    }
    // pagination outside a DataTable, which themes its own, keep antd's white tiles.
    ${theme.themeName === Theme.Dark
      ? css`
          .ant-pagination-item,
          .ant-pagination-item > a,
          .ant-pagination-prev .ant-pagination-item-link,
          .ant-pagination-next .ant-pagination-item-link {
            background: ${theme.buttonBackgroundColor};
            color: ${theme.headerColor};
          }
        `
      : ''}

    .ant-spin-dot-item {
      background-color: ${theme.teal};
    }
  `}

  ${({ theme }) =>
    theme.themeName === Theme.Dark
      ? css`
          html,
          body {
            scrollbar-color: ${theme.borderColor} ${theme.tertiaryBackgroundColor};

            ::-webkit-scrollbar-track {
              background-color: ${theme.tertiaryBackgroundColor};
            }

            ::-webkit-scrollbar-thumb {
              background-color: ${theme.borderColor};
            }

            h1,
            h2,
            h3,
            h4,
            h5,
            h6 {
              color: ${theme.headerColor};
            }

            h1.ant-typography,
            h2.ant-typography,
            h3.ant-typography,
            h4.ant-typography,
            h5.ant-typography,
            .ant-typography h1,
            .ant-typography h2,
            .ant-typography h3,
            .ant-typography h4,
            .ant-typography h5 {
              color: ${theme.headerColor};
            }

            div,
            p,
            span,
            i,
            em,
            b,
            strong {
              color: ${theme.textColor};
            }

            button,
            a {
              div,
              p,
              span,
              i,
              em,
              b,
              strong {
                color: unset;
              }
            }

            label {
              color: ${theme.labelColor};
            }

            .ant-typography.ant-typography-secondary {
              color: ${theme.secondaryTextColor};
            }
            .ant-typography.ant-typography-disabled {
              color: ${theme.disabledTextColor};
            }
          }
        `
      : ''}
`;
